"use client";

import { useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { CHAUFFEUR_HUB } from "./hubDict";

// Effet WebGL (ogl) chargé uniquement côté client, après le montage, pour ne
// pas alourdir le first-load JS. Le fond blanc de la section reste visible
// derrière (le hero n'est jamais vide).
const Threads = dynamic(() => import("@/components/react-bits/Threads"), {
  ssr: false,
});

type HeroSectionProps = {
  locale?: Locale;
  /**
   * Surcharge facultative de la cible du CTA primaire (et du CTA du bloc
   * réservation). Permet à la vue hub de repointer le CTA principal vers le
   * formulaire on-page (`#demande`) sans toucher au dictionnaire de contenu.
   * Défaut : la valeur éditoriale (d.ctaPrimary.href → /reservation).
   */
  ctaPrimaryHref?: string;
};

const HeroSection = ({
  locale = "fr",
  ctaPrimaryHref,
}: HeroSectionProps): JSX.Element => {
  const d = tr(CHAUFFEUR_HUB, locale).hero;
  const primaryHref = ctaPrimaryHref ?? d.ctaPrimary.href;
  // Gate de montage SSG-safe (false au SSR + 1er rendu, true après montage),
  // sans setState-in-effect (cf. règle react-hooks/set-state-in-effect).
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  return (
    <section className="relative flex min-h-screen items-center bg-white text-black">
      <div className="absolute inset-0 z-0">
        {mounted && (
          <Threads amplitude={1} distance={0} enableMouseInteraction={true} />
        )}
        <div className="absolute inset-0 bg-white/45" aria-hidden="true" />
      </div>
      <div className="relative z-10 mx-auto w-full max-w-[90rem] px-6 md:px-8">
        <div className="grid w-full items-center gap-10 py-16 pb-20 pt-32 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-black/60">
              {d.eyebrow}
            </p>
            <h1 className="mt-2 font-space-age text-3xl tracking-[0.2em] sm:text-4xl md:text-5xl lg:text-6xl">
              {d.title}
            </h1>
            {d.subtitle && (
              <p className="mt-4 max-w-2xl text-base text-black/80">
                {d.subtitle}
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={primaryHref}
                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-sm transition hover:bg-white/90"
              >
                {d.ctaPrimary.label}
              </a>
              <a
                href={d.ctaSecondary.href}
                className="inline-flex items-center justify-center rounded-full border border-black/20 px-6 py-3 text-sm font-semibold text-black transition hover:bg-black/5"
              >
                {d.ctaSecondary.label}
              </a>
            </div>

            <div className="mt-6 text-xs text-black/60">{d.trustLine}</div>
            <div className="mt-4 grid max-w-2xl gap-2 sm:grid-cols-3">
              {d.chips.map((item) => (
                <p
                  key={item}
                  className="rounded-lg border border-black/10 bg-black/[0.02] px-3 py-2 text-xs text-black/75"
                >
                  {item}
                </p>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 lg:justify-self-end">
            <div className="relative mx-auto w-full max-w-md overflow-hidden rounded-3xl border border-black/10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-100/90 via-white to-transparent p-6">
              <div className="mb-4 text-sm font-semibold text-black">
                {d.bookingTitle}
              </div>
              <ol className="space-y-3 text-sm">
                {d.steps.map((step, index) => (
                  <li key={step} className="flex gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/10 text-xs">
                      {index + 1}
                    </span>
                    <span className="text-black/80">{step}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-6">
                <a
                  href={primaryHref}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-black shadow-sm transition hover:bg-white/90"
                >
                  {d.bookingCta}
                </a>
                <p className="mt-2 text-center text-xs text-black/60">
                  {d.bookingNote}
                </p>
              </div>
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-black/5 blur-2xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
